import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import { 
  Users,
  Star,
  Heart,
  Shield,
  ArrowRight
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema
} from '../utils/schemaGenerator';

/**
 * MeetTheTeam Component
 * 
 * Framework for introducing team members.
 * DO NOT fabricate biographical details.
 * Only include verified information.
 */

const MeetTheTeamPage = () => {
  
  // Team member data structure
  // ONLY populate with verified information
  const teamMembers = [
    {
      name: "Bob",
      role: "Co-Owner & Operations Manager",
      bio: "Bob handles operations, scheduling, and customer relationships at Pierce Premier. When you call, you're likely speaking with Bob. He ensures every job is coordinated properly and every customer concern is addressed quickly.",
      expertise: [
        "Customer Relations",
        "Operations Management",
        "Scheduling Coordination",
        "Quality Oversight"
      ],
      verified: true // Only include if information is accurate
    },
    {
      name: "Heather",
      role: "Co-Owner & Executive Housekeeper",
      bio: "Heather brings over 15 years of executive housekeeping experience to Pierce Premier. She has worked with high-end properties and managed professional cleaning operations. Heather oversees quality standards, trains the team, and ensures every job meets her professional standards.",
      expertise: [
        "Executive Housekeeping (15+ years)",
        "Quality Standards",
        "Team Training",
        "High-End Property Experience"
      ],
      verified: true
    }
    // Add additional team members here ONLY when verified information is available
    // Example structure:
    // {
    //   name: "Team Member Name",
    //   role: "Role/Position",
    //   bio: "Brief professional background (verified only)",
    //   expertise: ["Skill 1", "Skill 2"],
    //   verified: true
    // }
  ];

  const companyValues = [
    "Consistency through same-team assignments",
    "Direct communication and accountability",
    "Professional training and oversight",
    "Long-term customer relationships",
    "Insured and bonded service",
    "Local ownership and community focus"
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Meet the Team | Pierce Premier Cleaning",
      description: "Meet Bob and Heather, the husband-and-wife team behind Pierce Premier. Learn about our experience, values, and commitment to Brevard County customers.",
      url: "/meet-the-team"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Meet the Team" }
    ]),
    generateLocalBusinessSchema()
  );

  return (
    <>
      <SEO 
        title="Meet the Team | Pierce Premier Cleaning"
        description="Meet Bob and Heather, the husband-and-wife team behind Pierce Premier. Learn about our experience, values, and commitment to Brevard County customers."
        canonical="/meet-the-team"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <Badge className="bg-blue-600 text-white">Locally Owned</Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Meet the Team Behind Pierce Premier
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              We're a husband-and-wife team serving Brevard County with professional cleaning services built on trust, consistency, and genuine relationships.
            </p>
          </div>
        </section>

        {/* Team Members */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-8">
              {teamMembers.filter(member => member.verified).map((member, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-8">
                    {/* Placeholder for future team photo */}
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <span className="text-white text-3xl font-bold">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                    
                    <div className="text-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{member.name}</h3>
                      <p className="text-blue-600 font-semibold">{member.role}</p>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-6 text-center">
                      {member.bio}
                    </p>
                    
                    {member.expertise && member.expertise.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-600 mb-3 text-center">Expertise:</h4>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {member.expertise.map((skill, skillIdx) => (
                            <Badge key={skillIdx} variant="outline" className="text-gray-700">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Company Values */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Guides Our Work
              </h2>
              <p className="text-gray-600">The principles we follow in every job</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {companyValues.map((value, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-blue-100">
                  <Star className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Work With Us */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-6">
                  <Heart className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    Why Customers Work With Pierce Premier
                  </h2>
                </div>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    When you work with Pierce Premier, you're working directly with us—Bob and Heather. We're not a franchise, and we're not trying to scale into the biggest cleaning company in Florida. We're focused on building long-term relationships with customers who value consistency, trust, and direct communication.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    You'll have the same cleaning team every visit, our personal cell numbers for questions or scheduling, and the peace of mind that comes from working with people who are accountable, responsive, and genuinely invested in your satisfaction.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 text-center">
                <CardContent className="p-6">
                  <Shield className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Insured & Bonded</h3>
                  <p className="text-gray-600 text-sm">Fully insured and bonded for your protection and peace of mind</p>
                </CardContent>
              </Card>
              <Card className="border-2 text-center">
                <CardContent className="p-6">
                  <Users className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Same Team</h3>
                  <p className="text-gray-600 text-sm">Consistent cleaners who learn your home and preferences</p>
                </CardContent>
              </Card>
              <Card className="border-2 text-center">
                <CardContent className="p-6">
                  <Star className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">15+ Years Experience</h3>
                  <p className="text-gray-600 text-sm">Professional executive housekeeping expertise</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Work With Pierce Premier?"
          description="Experience the difference of working with a locally-owned, relationship-focused cleaning company."
          primaryButtonText="Get Your Free Quote"
        />

        {/* Learn More Links */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Learn More About Pierce Premier
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Why Choose Us", link: "/why-choose-us" },
                { name: "Our Services", link: "/services" },
                { name: "Service Areas", link: "/areas" },
                { name: "Reviews", link: "/reviews" },
                { name: "FAQ", link: "/faq" },
                { name: "Contact Us", link: "/contact" }
              ].map((item, idx) => (
                <Link 
                  key={idx} 
                  to={item.link} 
                  className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 hover:border-blue-400 transition-colors text-center group"
                >
                  <span className="text-blue-700 font-semibold group-hover:text-blue-900">{item.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-blue-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MeetTheTeamPage;

/**
 * EXPANSION INSTRUCTIONS:
 * 
 * To add additional team members in the future:
 * 
 * 1. Add new object to teamMembers array above
 * 2. ONLY include verified, factual information
 * 3. Structure:
 *    {
 *      name: "First Name or Full Name",
 *      role: "Official Role/Title",
 *      bio: "Brief professional background (2-3 sentences)",
 *      expertise: ["Skill 1", "Skill 2", "Skill 3"],
 *      verified: true // ONLY set true when information confirmed
 *    }
 * 
 * 4. Optional: Add team member photo
 *    - Replace the gradient circle placeholder with:
 *      <img 
 *        src="/path/to/photo.jpg" 
 *        alt="Name - Role at Pierce Premier" 
 *        className="w-24 h-24 rounded-full object-cover mb-4 mx-auto"
 *      />
 * 
 * DO NOT:
 * - Invent biographical details
 * - Exaggerate qualifications
 * - Add team members without verified information
 * - Use stock photos and claim they are team members
 */
